import React, { FC, useCallback } from 'react';
import { Typography, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { get } from 'lodash';

import DateInput from '~/components/inputs/DateInput';
import { Form, DocumentInput, NationalitySelect, RelationSelect } from '~/components';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import theme from '~/constants/theme';
import { NamePath } from '~/utils/buildNamePath';
import ReviewItem from '~/pages/ksa/review/components/ReviewItem';
import ReviewItemCheckbox from '~/pages/ksa/review/components/ReviewItemCheckbox';
import { commonNames } from '~/pages/ksa/application/managers/components/PersonFormItems';
import { KsaPerson } from '~/types/ksa/KsaPerson';
import { useGetPayload } from '~/controllers/wizard';
import ShouldUpdate from '~/components/Form/ShouldUpdate';
import WithPep from '~/components/WithPep';
import { isEnglish } from '~/utils/isEnglish';
import { useGetlocalizedValue } from '~/hooks/useGetlocalizedValue';
import { detectFieldIssue } from '~/utils/detectFieldIssue';

/** lodash merge skips `undefined` but writes `null`.
 *  This wrapper ensures that clearing the date picker sends null to the form
 *  so merge properly overwrites the old value in the payload. */
const NullableDateInput: FC<any> = ({ onChange, ...rest }) => (
  <DateInput {...rest} onChange={(val: string | undefined) => onChange?.(val ?? null)} />
);


interface Props {
  formItemsPath: NamePath;
  payloadReferencePath: NamePath;
  isSignedWithEmail: boolean;
  hasVerifiedPhoneNumber: boolean;
  hasPassport?: boolean;
  hasPersonalDocument?: boolean;
  showRelationshipType?: boolean;
}

const PersonFormItems: FC<Props> = (props) => {
  const {
    formItemsPath,
    payloadReferencePath,
    isSignedWithEmail,
    hasVerifiedPhoneNumber,
    hasPassport,
    hasPersonalDocument,
    showRelationshipType,
  } = props;
  const { t } = useTranslation('ksaReview');

  const n = useCallback(
    (namePath: NamePath) => [formItemsPath].flat().concat([namePath].flat()),
    [formItemsPath]
  );
  const payload = useGetPayload();
  const person: any = get(payload, payloadReferencePath);

  const isAuthorizedSignatory = person?.personalData?.isAuthorizedSignatory;
  const idType = person?.personalDocument?.idType;
  const { localizedValue: relationLocalizedValue, isReady: isReadyRelation }: any =
    useGetlocalizedValue(person?.personalData?.relation, 'relation');
  const { localizedValue: nationalityLocalizedValue, isReady: isReadyNationality }: any =
    useGetlocalizedValue(person?.personalData?.nationality, 'nationality');

  return (
    <Container>
      <Form.Item name={n(commonNames.uid)} hidden initialValue={person?.uid} />
      <Section formItemFirst formItemLast>
        <Typography.Title level={4}>{t('Personal data').toUpperCase()}</Typography.Title>
        <InputsGroupLayout>
          <Form.Item
            label={t('Full name – Agent Updates')}
            name={n(['personalData', 'englishFullName'])}
            initialValue={
              person?.personalData?.englishFullName || person?.personalData?.fullName || undefined
            }
            rules={[{ required: true, message: t('Please enter the English full name') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('Nationality – Agent Updates')}
            name={n(['personalData', 'englishNationality'])}
            initialValue={
              isEnglish(nationalityLocalizedValue) ? person?.personalData?.nationality : undefined
            }
            rules={[{ required: true, message: t('Please select the nationality') }]}
          >
            <NationalitySelect />
          </Form.Item>
          {person?.personalData?.relation && (
            <Form.Item
              label={t('Relation')}
              name={n(['personalData', 'englishRelation'])}
              initialValue={
                isEnglish(relationLocalizedValue) ? person?.personalData?.relation : undefined
              }
            >
              <RelationSelect />
            </Form.Item>
          )}
          {/* Date of birth — optional field for agent updates */}
          <Form.Item
            labelText={t('Date of birth – Agent Updates')}
            name={n(['personalData', 'agentDateOfBirth'])}
            initialValue={person?.personalData?.dateOfBirth || undefined}
          >
            <NullableDateInput />
          </Form.Item>
          {/* Manager Relationship Type — shown in Manager tab always, or when Thiqah returns positions data */}
          {(showRelationshipType || person?.personalData?.positions?.length > 0) && (
            <Form.Item
              labelText={t('Manager Relationship Type')}
              name={n(['personalData', 'agentRelationshipType'])}
              initialValue={person?.personalData?.positions?.[0]?.name || undefined}
              rules={[{ required: true, message: t('Please enter the relationship type') }]}
            >
              <Input placeholder={t('Enter relationship type')} />
            </Form.Item>
          )}
        </InputsGroupLayout>
      </Section>
      {isAuthorizedSignatory && (
        <Section>
          <Typography.Title level={4}>
            {t('Authorised Signatory Proof').toUpperCase()}
          </Typography.Title>

          <ShouldUpdate name={n(['personalData', 'isAuthorizedSignatory'])}>
            {(form) => {
              return (
                <>
                  <InputsGroupLayout>
                    {isSignedWithEmail ? (
                      <ReviewItem label={t('Email')}>{person?.personalData?.email}</ReviewItem>
                    ) : (
                      <ReviewItem
                        label={t('Email')}
                      // name={n(['personalData', 'email'])}
                      // disabled
                      >
                        {person?.personalData?.email}
                      </ReviewItem>
                    )}
                    {hasVerifiedPhoneNumber ? (
                      <ReviewItem label={t('Phone number')}>
                        {person?.personalData?.phoneNumber && (
                          <bdo dir="ltr">{person?.personalData?.phoneNumber}</bdo>
                        )}
                      </ReviewItem>
                    ) : (
                      <ReviewItem
                        label={t('Phone number')}
                      // name={n(['personalData', 'phoneNumber'])}
                      // disabled
                      >
                        {person?.personalData?.phoneNumber && (
                          <bdo dir="ltr">+971{person?.personalData?.phoneNumber}</bdo>
                        )}
                      </ReviewItem>
                    )}
                  </InputsGroupLayout>
                  <ReviewItem
                    label={t('Authorised signatory proof type')}
                  // name={n(['personalData', 'proof', 'documentType'])}
                  // disabled
                  >
                    {person?.proof && t(person?.proof)}
                  </ReviewItem>

                  {!!person?.files?.length && (
                    <ReviewItem
                      label={t('Authorised Signatory Proof Files')}
                    // name={n(['personalData', 'proof', 'files'])}
                    // disabled
                    >
                      <div onClick={(e) => e.preventDefault()} aria-hidden>
                        <DocumentInput
                          name=""
                          maxCount={person.files.length}
                          value={person.files}
                          noRemove
                          multiple={false}
                          disabled
                        />
                      </div>
                      )
                    </ReviewItem>
                  )}
                </>
              );
            }}
          </ShouldUpdate>
        </Section>
      )}
      {hasPassport && (
        <Section>
          <Typography.Title level={4}>{t('Passport').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            <ReviewItem label={t('Document number')}>{person?.passport?.documentNumber}</ReviewItem>

            <ReviewItem
              label={t('Passport issue date')}
            // name={n(['passport', 'issueDate'])}
            >
              {person?.passport?.issueDate}
            </ReviewItem>
            <ReviewItem
              label={t('Passport expiry date')}
            // name={n(['passport', 'expiryDate'])}
            >
              {person?.passport?.expiryDate}
            </ReviewItem>
          </InputsGroupLayout>
        </Section>
      )}
      <Section formItemLast>
        {hasPersonalDocument ? (
          <>
            <Typography.Title level={4}>{t('Personal Document').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              {/* ID Type Agent Updates field added (Risk Team requirement): Thiqah can return it in Arabic or blank */}
              <Form.Item
                labelText={t('ID Type')}
                name={n(['personalDocument', 'agentIdType'])}
                initialValue={person?.personalDocument?.idType || undefined}
                rules={[{ required: true, message: t('Please enter the ID type') }]}
              >
                <Input placeholder={t('Enter ID type')} />
              </Form.Item>
              {/* ID Number is required for compliance; made editable only when Thiqah returns it missing or in Arabic */}
              <Form.Item
                labelText={t('ID Number – Agent Update')}
                name={n(['personalDocument', 'agentIdNumber'])}
                initialValue={person?.personalDocument?.idNumber || undefined}
                rules={[{ required: true, message: t('Please enter the ID number') }]}
              >
                <Input placeholder={t('Enter ID number')} />
              </Form.Item>
            </InputsGroupLayout>
          </>
        ) : (
          /* Thiqah returned no personal document at all — expose a plain input so the agent can still record the ID number */
          <>
            <Typography.Title level={4}>{t('Personal Document').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              <Form.Item
                labelText={t('ID Number – Agent Update')}
                name={n(['personalDocument', 'agentIdNumber'])}
                rules={[{ required: true, message: t('Please enter the ID number') }]}
              >
                <Input placeholder={t('Enter ID number')} />
              </Form.Item>
            </InputsGroupLayout>
          </>
        )}
      </Section>
    </Container>
  );
};

// export const uploadPowerOfAttorney =
//   'Upload a copy of the Power of Attorney showing this person as an authorised signatory';
// const getProofOptions = () => [
//   {
//     label: i18n.t(
//       'This person is listed on the Company License as part of the company ownership structure',
//       { ns: 'ksaApplication' }
//     ),
//     value:
//       'This person is listed on the Company License as part of the company ownership structure',
//   },
//   {
//     label: i18n.t(uploadPowerOfAttorney, { ns: 'ksaApplication' }),
//     value: uploadPowerOfAttorney,
//   },
// ];

const Container = styled.div`
  @media (max-width: ${theme.breakpoints.md}px) {
    .AuthorizedRepresentativeFormItems-DocumentTypeSelect {
      max-width: calc(100vw - ${32 * 2}px);
    }
  }
  @media (max-width: ${theme.breakpoints.sm}px) {
    .AuthorizedRepresentativeFormItems-DocumentTypeSelect {
      max-width: calc(100vw - ${12 * 2 + 12 * 2}px);
    }
  }
`;

export default PersonFormItems;
